import { z } from "zod";
import { ArchetypeIndex, LessonId, StepId, TemplateId } from "./learn.js";
import { StructuredNote } from "./note.js";
import { GroupQuote } from "./groupVerdicts.js";
import { BlindGuess } from "./tasting.js";
import { WineIdentity } from "./wine.js";

export { FlightJoinCode } from "./join.js";
import { FlightJoinCode } from "./join.js";

/** The comparative formats a lesson or template can frame a flight with; `single` never makes a flight. */
export const FlightFormat = z.enum(["horizontal", "vertical", "diagonal"]);
export type FlightFormat = z.infer<typeof FlightFormat>;

/** One wine slot of a comparison, described to every participant in both locales, in our words. */
export const FlightArchetype = z.object({
  index: ArchetypeIndex,
  nl: z.string().min(1).max(120),
  en: z.string().min(1).max(120),
});
export type FlightArchetype = z.infer<typeof FlightArchetype>;

const archetypesAreSlots = (archetypes: readonly { readonly index: number }[]): boolean =>
  archetypes.every(
    (a, i) => archetypes.some((other) => other.index === i) && a.index < archetypes.length,
  ) && new Set(archetypes.map((a) => a.index)).size === archetypes.length;

const Archetypes = z
  .array(FlightArchetype)
  .min(2)
  .max(6)
  .refine(archetypesAreSlots, { error: "archetype indexes are 0 to n-1, each once" });

/** A flight started from a lesson's taste step: the framing every participant sees (request and echo). */
export const LessonFlightRef = z.object({
  lessonId: LessonId,
  stepId: StepId,
  format: FlightFormat,
  archetypes: Archetypes,
});
export type LessonFlightRef = z.infer<typeof LessonFlightRef>;

/** A flight started from a template: the request is one id, the edge resolves the rest. */
export const TemplateFlightRef = z.object({ templateId: TemplateId });
export type TemplateFlightRef = z.infer<typeof TemplateFlightRef>;

/** What a template flight echoes: the id with the format and archetypes resolved at creation. */
export const TemplateFlightEcho = z.object({
  templateId: TemplateId,
  format: FlightFormat,
  archetypes: Archetypes,
});
export type TemplateFlightEcho = z.infer<typeof TemplateFlightEcho>;

export const CreateFlightRequest = z
  .object({
    title: z.string().min(1).max(120),
    blind: z.boolean().default(false),
    /** The host is auto-joined as the flight's first participant. */
    hostDisplayName: z.string().min(1).max(40).default("Host"),
    /** Started from a lesson's comparative step; exclusive with `template`. */
    lesson: LessonFlightRef.exactOptional(),
    /** Started from a template of the comparative library; exclusive with `lesson`. */
    template: TemplateFlightRef.exactOptional(),
    /** A flight can be made before any bottle is picked (the link is shared first); it needs one
     * to start, and more can be added until it is done. With a lesson or template, every
     * wine carries the archetype slot it fills and there are two to six. */
    wines: z
      .array(
        z.union([
          z.object({ wineId: z.string().min(1), archetypeIndex: ArchetypeIndex.exactOptional() }),
          z.object({ wine: WineIdentity, archetypeIndex: ArchetypeIndex.exactOptional() }),
        ]),
      )
      .max(24),
  })
  .superRefine((request, ctx) => {
    const framed = request.lesson !== undefined || request.template !== undefined;
    if (request.lesson !== undefined && request.template !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["template"],
        message: "a flight is made from a lesson or from a template, not both",
      });
    }
    if (!framed) {
      request.wines.forEach((wine, i) => {
        if (wine.archetypeIndex !== undefined) {
          ctx.addIssue({
            code: "custom",
            path: ["wines", i, "archetypeIndex"],
            message: "archetype slots belong to flights made from a lesson or a template",
          });
        }
      });
      return;
    }
    const slots = request.lesson?.archetypes.length ?? request.wines.length;
    if (request.wines.length < 2 || request.wines.length > 6 || request.wines.length !== slots) {
      ctx.addIssue({
        code: "custom",
        path: ["wines"],
        message: `a comparison needs one bottle per archetype, two to six in all (${String(slots)} expected)`,
      });
      return;
    }
    const taken = new Set<number>();
    request.wines.forEach((wine, i) => {
      const slot = wine.archetypeIndex;
      if (slot === undefined || slot >= slots || taken.has(slot)) {
        ctx.addIssue({
          code: "custom",
          path: ["wines", i, "archetypeIndex"],
          message: "every bottle fills its own archetype slot, 0 to n-1",
        });
      } else {
        taken.add(slot);
      }
    });
  });
export type CreateFlightRequest = z.infer<typeof CreateFlightRequest>;

export const AddFlightWineRequest = z.union([
  z.object({ wineId: z.string().min(1) }),
  z.object({ wine: WineIdentity }),
]);
export type AddFlightWineRequest = z.infer<typeof AddFlightWineRequest>;

export const JoinFlightRequest = z.object({
  displayName: z.string().min(1).max(40),
});
export type JoinFlightRequest = z.infer<typeof JoinFlightRequest>;

export const SubmitFlightNoteRequest = z.object({
  flightWineId: z.string().min(1),
  note: StructuredNote,
  depth: z.enum(["quick", "deep"]),
  blindGuess: BlindGuess.optional(),
  /** A line the taster chose to say to the group about this wine; never taken from the note. */
  groupQuote: GroupQuote.exactOptional(),
});
export type SubmitFlightNoteRequest = z.infer<typeof SubmitFlightNoteRequest>;

export const FlightStatus = z.enum(["lobby", "active", "done"]);
export type FlightStatus = z.infer<typeof FlightStatus>;

export const FlightWineView = z.object({
  id: z.string().min(1),
  index: z.number().int().nonnegative(),
  /** `null` until this wine is revealed (blind flights) or always the real
   * wine (non-blind flights show it from the start). */
  wine: WineIdentity.nullable(),
  revealed: z.boolean(),
  /** How many participants have already submitted a note for this wine —
   * enough for the host's lobby UI ("3 of 5 done") without leaking notes. */
  notesSubmitted: z.number().int().nonnegative(),
  /** The slot this wine fills on a flight made from a lesson or a template. */
  archetypeIndex: ArchetypeIndex.exactOptional(),
});
export type FlightWineView = z.infer<typeof FlightWineView>;

export const FlightParticipantView = z.object({
  deviceId: z.string().min(1),
  displayName: z.string(),
  isHost: z.boolean(),
  joinedAt: z.string().datetime(),
  /** Present (true) only for someone who left the flight; their notes stay. */
  left: z.literal(true).exactOptional(),
});
export type FlightParticipantView = z.infer<typeof FlightParticipantView>;

/** The full poll-able state of a flight — `GET /v1/flights/:code`. Clients
 * (host lobby, participant waiting room, in-progress note screen) all poll
 * this one shape and derive their own view from `status`/`currentIndex`. */
export const FlightState = z.object({
  id: z.string().min(1),
  joinCode: FlightJoinCode,
  title: z.string(),
  blind: z.boolean(),
  status: FlightStatus,
  currentIndex: z.number().int().nonnegative(),
  isHost: z.boolean(),
  hasSubmittedCurrent: z.boolean(),
  wines: z.array(FlightWineView),
  participants: z.array(FlightParticipantView),
  createdAt: z.string().datetime(),
  /** The lesson framing, shown to every participant. */
  lesson: LessonFlightRef.exactOptional(),
  /** The template framing, shown to every participant. */
  template: TemplateFlightEcho.exactOptional(),
});
export type FlightState = z.infer<typeof FlightState>;

/** One participant's note on one wine, for the results screen — always
 * scoped to a `done` flight, and the wine is always revealed by then. */
export const FlightResultNote = z.object({
  deviceId: z.string().min(1),
  displayName: z.string(),
  note: StructuredNote,
  blindGuess: BlindGuess.optional(),
  blindCorrect: z.boolean().nullable(),
});
export type FlightResultNote = z.infer<typeof FlightResultNote>;

export const FlightResultWine = z.object({
  flightWineId: z.string().min(1),
  index: z.number().int().nonnegative(),
  wine: WineIdentity,
  notes: z.array(FlightResultNote),
  archetypeIndex: ArchetypeIndex.exactOptional(),
});
export type FlightResultWine = z.infer<typeof FlightResultWine>;

export const FlightResults = z.object({
  flight: FlightState,
  wines: z.array(FlightResultWine),
});
export type FlightResults = z.infer<typeof FlightResults>;
